/**
 * Container for the Export ▾ menu (PV18, SE-8360).
 *
 * Owns the menu's open state and builds the CSV / PDF rows from the export
 * controller. When the controller says the platform cannot hand a file off
 * (`available: false`, native in v1) each row is disabled and captioned
 * "Available on web" — stated on screen, never a silent no-op.
 * @module features/scouting/components/WatchlistExportMenu/WatchlistExportMenuContainer
 */

import { FileTextIcon, TableIcon } from "lucide-react-native";
import { useCallback, useMemo, useState } from "react";

import type { DesktopDropdownMenuItem } from "@/components/molecules/DesktopDropdownMenu";
import type {
  WatchlistExportController,
  WatchlistExportFormat,
} from "@/features/scouting/hooks/useWatchlistExport";
import { TEST_ID_WATCHLIST_EXPORT } from "e2e/testId";

import WatchlistExportMenuView from "./WatchlistExportMenuView";

/** Props for {@link WatchlistExportMenuContainer}. */
export interface WatchlistExportMenuProps {
  readonly controller: WatchlistExportController;
  /**
   * The formats offered, in order. Default: CSV · PDF behind Export ▾. A
   * single format renders as a plain "Export {FORMAT}" button with no menu
   * (PV19, SE-8361 / PRD-4.1: the Pitch has "Export PDF in place of Export ▾").
   */
  readonly formats?: readonly WatchlistExportFormat[];
  /** SE-8535: the single-format button drops its label (toolbar density 3). */
  readonly iconOnly?: boolean;
}

/** What a format row says when the platform cannot write a file. */
const UNAVAILABLE_CAPTION = "Available on web";

/** The two formats, in the PRD's order. */
const FORMAT_ENTRIES: readonly {
  readonly format: WatchlistExportFormat;
  readonly label: string;
  readonly icon: typeof TableIcon;
}[] = [
  { format: "csv", label: "CSV", icon: TableIcon },
  { format: "pdf", label: "PDF", icon: FileTextIcon },
];

/** Every format, the Table's default. */
const ALL_FORMATS: readonly WatchlistExportFormat[] = FORMAT_ENTRIES.map(
  entry => entry.format
);

/**
 * Builds the rows and renders the View.
 * @param props - Component props
 * @param props.controller - The export operation
 * @param props.formats - The formats offered, in order
 * @param props.iconOnly - SE-8535: the single-format button drops its label
 */
const WatchlistExportMenuContainer = ({
  controller,
  formats = ALL_FORMATS,
  iconOnly = false,
}: WatchlistExportMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = useCallback(() => setIsMenuOpen(open => !open), []);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const { available, start } = controller;

  const items = useMemo<readonly DesktopDropdownMenuItem[]>(
    () =>
      FORMAT_ENTRIES.filter(entry => formats.includes(entry.format)).map(
        ({ format, label, icon }) => ({
          id: format,
          label,
          icon,
          testID: TEST_ID_WATCHLIST_EXPORT.itemFor(format),
          disabled: !available,
          ...(available ? {} : { keyboardShortcut: UNAVAILABLE_CAPTION }),
          onPress: () => start(format),
        })
      ),
    [available, formats, start]
  );
  // One format: the trigger IS the action.
  const single = items.length === 1 ? items[0] : null;
  const singleFormat = useMemo(
    () =>
      single
        ? {
            label: `Export ${single.label}`,
            caption: available ? null : UNAVAILABLE_CAPTION,
            disabled: !available,
            onPress: single.onPress,
          }
        : null,
    [available, single]
  );

  return (
    <WatchlistExportMenuView
      status={controller.status}
      progress={controller.progress}
      error={controller.error}
      filename={controller.filename}
      isMenuOpen={isMenuOpen}
      onToggleMenu={toggleMenu}
      onCloseMenu={closeMenu}
      items={items}
      singleFormat={singleFormat}
      onRetry={controller.retry}
      onCancel={controller.cancel}
      iconOnly={iconOnly}
    />
  );
};

WatchlistExportMenuContainer.displayName = "WatchlistExportMenuContainer";

export default WatchlistExportMenuContainer;
